import { useCallback, useEffect, useRef, useState } from "react";
import { formatTimecode, timecodeAt, timecodeSpec } from "@infoflyt/shared/timecode";
import type { Oekt } from "../state/session";
import * as logg from "../state/logg";
import * as su from "../state/su";
import { useLevende } from "../state/levende";
import { useSkrivende } from "../state/skrivende";

/**
 * Redaktørens loggflate.
 *
 * ÉN BOKS PER REGISSØR, ved siden av hverandre som beskjedfeltene. Tre
 * regier ute er tre bokser — ikke én logg man bytter mellom, og ikke én per
 * loggøkt. Økta er en teknisk enhet; redaktøren leser personer.
 *
 * POSTEN SER UT SOM I APPEN. Dette er en LOGG, ikke en chat: posten er en
 * boks med fyll og uten kant, tidskoden står øverst, etiketten ved siden av
 * den, og teksten i samme skrift som regi skrev den i. Det er de samme
 * tokenene (`--content-rest`, `--r-content`) og de samme målene som
 * feltappens `.entry`. Den som leser loggen her og på telefonen skal lese
 * DET SAMME, ikke to utgaver av det.
 *
 * REDAKTØREN LIGGER TIL HØYRE, i en annen farge. Det er det eneste stedet
 * formen låner fra en meldingsapp, og det er fordi den sier noe sant:
 * venstresiden er loggen, høyresiden er det ikke. De to kan ikke forveksles
 * — heller ikke når rapporten leses om et år.
 *
 * LUFTEN MELLOM POSTENE BÆRER SAMMENHENGEN. Tolv piksler mellom to poster,
 * fire mellom en post og svaret på den. Da ser man hva som hører sammen uten
 * at noe er tegnet rundt det.
 *
 * INGEN TIDSSKILLER. En meldingsapp setter inn et klokkeslett når det har
 * gått en stund, fordi en samtale har pauser som betyr noe. En logg har
 * tidskode på hver eneste post — skillene sa ingenting som ikke alt sto
 * der, og de delte loggen opp i bolker som ikke finnes.
 *
 * LESES OGSÅ I ETTERKANT. Dagvelgeren i toppstripa styrer hvilken dag som
 * vises, og en lukket logg er like lesbar som en som går. Alderslinja sier
 * «lukket» i stedet for å telle minutter på noe som er over.
 *
 * INGEN SLETTEKNAPP, OG DET ER EN REGEL. En loggpost slettes bare i appen,
 * av den som skrev den — se CLAUDE.md. Grunnen er at loggen går ÉN VEI: opp.
 * Telefonen er originalen, dette er kopien de andre leser, og appen tar
 * aldri imot endringer i sin egen logg. Fjernes en post bare her, står den
 * igjen på telefonen, og de to viser ulik logg uten at noe sier fra. Det
 * skjedde én gang, da jeg ryddet testposter rett i basen.
 */

/* Over dette er bildet gammelt nok til å si fra om. Regi står ofte uten
   dekning, og da skal det stå at spalten har stanset — ikke se ut som om
   det er stille på settet. Gjelder bare logger som er ÅPNE. */
const GAMMELT_MS = 4 * 60 * 1000;

function klokke(ms: number): string {
  const d = new Date(ms);
  return `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
}

function alder(ms: number, naa: number): string {
  const min = Math.floor((naa - ms) / 60000);
  if (min < 1) return "nå";
  if (min < 60) return `${min} min siden`;
  const t = Math.floor(min / 60);
  return `${t} t siden`;
}

/** Tidskoden for én post, regnet av postens eget anker. */
function tidskode(p: logg.Loggpost): string {
  const a = p.anker;
  if (a === null) return "--:--:--:--";
  const rate = a.framerate as Parameters<typeof timecodeSpec>[0];
  const spec = timecodeSpec(rate, a.dropFrame);
  const t = timecodeAt(
    {
      tcFrames: a.tcFrames,
      monotonicMs: a.monotonicMs,
      bootId: a.bootId,
      wallUtcMs: a.wallUtcMs,
      framerate: rate,
      dropFrame: a.dropFrame,
      source: a.source === "ltc" || a.source === "ble" ? a.source : "manual",
    },
    { monotonicMs: p.monotonicMs, bootId: p.bootId, wallUtcMs: p.wallUtcMs },
    spec,
  );
  return formatTimecode(t.frames, spec);
}

type Boks = { readonly post: string; readonly hva: "kommentar" | "synk" };

/**
 * Hvem som kan kommentere, spoerre og gi hjerte HERFRA.
 *
 * ========  DET ER REDAKTOERENS VERKTOEY  ========
 *
 * Brukeren 03.11.2026: «Det er bare redaktør som skal kunne skrive
 * kommentarer, spørsmål og like loggposter fra logg.»
 *
 * Basen har sagt det samme siden 29.10.2026 — `tilgang.veileder` er
 * `redaktor` eller `produsent`, og det er den policyen `entry_comment` og
 * `entry_mark` henger paa. Flata viste likevel knappene til alle, og en
 * prodleder som skrev en kommentar fikk den avvist av RLS. `send` svarte
 * `return` uten et ord. Det er HELE forklaringen paa de to andre tingene
 * brukeren meldte samme dag: «enter sender ikke» og «kommentaren
 * forsvinner naar jeg skriver et synkspoersmaal» — kommentaren ble aldri
 * skrevet, og spoersmaalet ble det, fordi `su_question` staar aapen for
 * alle medlemmer (det er den regi skriver fra appen).
 *
 * Produsenten beholder det. Hen har alle noekler overalt ellers, og en
 * regel som sa noe annet HER ville vaert den eneste.
 */
function kanVeilede(oekt: Oekt): boolean {
  const alle = [oekt.member.role, ...(oekt.member.extraRoles ?? [])];
  return alle.includes("redaktor") || alle.includes("produsent");
}

export function LoggScreen({
  oekt, opptaksdag,
}: {
  readonly oekt: Oekt;
  readonly opptaksdag: string;
}): JSX.Element {
  const produksjonId = oekt.production.id;
  const [spalter, setSpalter] = useState<readonly logg.Loggspalte[]>([]);
  const [laster, setLaster] = useState(true);
  const [deltakere, setDeltakere] = useState<readonly su.Deltaker[]>([]);
  const [aapen, setAapen] = useState<Boks | null>(null);
  const [utkast, setUtkast] = useState("");
  const [deltakerId, setDeltakerId] = useState<string | null>(null);
  const [feil, setFeil] = useState("");
  /* Tikker bare for aldersteksten. Ett minutt er presist nok for «6 min
     siden», og et sekundtikk ville tegnet hele flata på nytt. */
  const [naa, setNaa] = useState(() => Date.now());

  const last = useCallback((): void => {
    void (async () => {
      const d = await logg.hentLogger(oekt, opptaksdag);
      setSpalter(d);
      setLaster(false);
    })();
  }, [oekt, opptaksdag]);

  /*
   * ========  «HENTER LOGGENE …» SKAL IKKE BLINKE  ========
   *
   * Brukeren 03.11.2026: «Naar jeg gaar vekk fra siden en stund og gaar
   * tilbake, saa forsvinner loggen et lite sekund mens det staar 'henter
   * loggen'. Det er noe man maa vente paa foer man kan skrive, som er
   * plagsomt.»
   *
   * Her sto `setLaster(true)` i effekten — og effekten kjoerer hver gang
   * `last` er ny, altsaa hver gang `oekt` er et nytt objekt. Det skjer ved
   * hver fornying av innloggingen OG hver gang dagslista hentes paa nytt,
   * som er ved hver eneste loggpost. Loggen ble da revet bort og tegnet opp
   * igjen mens den som leser holdt paa.
   *
   * Meldingen hoerer til FOERSTE henting. Alt etter det er en oppfriskning,
   * og den skal skje under det som alt staar.
   */
  const harLastet = useRef(false);
  useEffect(() => {
    if (!harLastet.current) setLaster(true);
    harLastet.current = true;
    last();
  }, [last]);

  useEffect(() => {
    void (async () => { setDeltakere((await su.hent(produksjonId)).deltakere); })();
  }, [produksjonId]);

  useEffect(() => {
    const id = window.setInterval(() => setNaa(Date.now()), 60000);
    return () => { window.clearInterval(id); };
  }, []);

  useLevende(
    "logg",
    ["log_entry", "log_session", "tc_anchor", "entry_comment", "entry_mark"],
    last,
  );

  const skrivende = useSkrivende(produksjonId, opptaksdag);
  const veileder = kanVeilede(oekt);

  const send = useCallback(async (postId: string, hva: "kommentar" | "synk"): Promise<void> => {
    const tekst = utkast.trim();
    if (tekst === "") return;
    const ok = hva === "kommentar"
      ? await logg.skrivKommentar(postId, oekt.member.id, tekst)
      : await logg.skrivSpoersmaal(produksjonId, postId, deltakerId, tekst);
    /*
     * EN AVVIST SKRIVING SIER FRA.
     *
     * Her sto bare `return`. Teksten ble staaende i boksen, ingenting
     * skjedde, og det saa ut som at Enter ikke virket — se `kanVeilede`.
     * Utkastet blir staaende her ogsaa, men naa med et svar.
     */
    if (!ok) {
      setFeil(hva === "kommentar"
        ? "Kommentaren ble ikke lagret. Det er redaktøren som svarer regi herfra."
        : "Spørsmålet ble ikke lagret. Prøv igjen.");
      return;
    }
    setFeil("");
    setUtkast("");
    setAapen(null);
    last();
  }, [utkast, oekt.member.id, produksjonId, deltakerId, last]);

  const veksleMerke = useCallback(async (p: logg.Loggpost): Promise<void> => {
    if (!await logg.settMarkering(p.id, oekt.member.id, !p.markert)) {
      setFeil("Hjertet ble ikke lagret. Det er redaktørens merke.");
      return;
    }
    setFeil("");
    last();
  }, [oekt.member.id, last]);

  const aapne = useCallback((post: string, hva: "kommentar" | "synk"): void => {
    setUtkast("");
    setDeltakerId(null);
    setAapen((f) => (f !== null && f.post === post && f.hva === hva ? null : { post, hva }));
  }, []);

  if (laster) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">Henter loggene …</p>
      </div></div>
    );
  }

  if (spalter.length === 0) {
    return (
      <div className="flate"><div className="flate__innhold">
        <p className="synk__melding">
          Ingen logg på opptaksdag {opptaksdag}. Her kommer én boks per
          regissør — både mens det logges og etterpå.
        </p>
      </div></div>
    );
  }

  return (
    <div className="flate">
      <div className="flate__innhold">
        {feil === "" ? null : (
          <p className="synk__melding synk__melding--feil">{feil}</p>
        )}
        <div className="logger">
          {spalter.map((s) => {
            const gammel = s.aapen && s.sistePost !== null && naa - s.sistePost > GAMMELT_MS;
            const skriver = s.oektIder.some((id) => skrivende.has(id));
            return (
              <section
                className="loggkort"
                key={s.id}
                aria-label={`Logg fra ${s.lag ?? s.forfatter}`}
              >
                <header className="loggkort__hode">
                  <span className="loggkort__merke mono">{s.initialer}</span>
                  {/*
                    * TEAMET ER SPALTA, IKKE PERSONEN.
                    *
                    * Her sto forfatterens navn stort og teamet som en
                    * bimerknad. Men en teamlogg er TEAMETS: hele LET 2
                    * skriver i den, og navnet paa oekta er regiens bare
                    * hvis det finnes en regi paa teamet — ellers staar
                    * den som rakk aa aapne den, ofte fotografen.
                    * Brukeren 02.11.2026 saa nettopp det.
                    *
                    * Naa staar teamet foerst, og regien paa teamet ved
                    * siden av. Uten team er loggen én persons, og da er
                    * navnet hens riktig tittel.
                    */}
                  <span className="loggkort__navn">{s.lag ?? s.forfatter}</span>
                  {s.lag === null ? null : (
                    <span className="loggkort__lag">
                      {s.regi ?? "ingen regi på teamet"}
                    </span>
                  )}
                  {/*
                    * HVOR GAMMELT BILDET ER, ikke om vi er tilkoblet. En
                    * åpen logg som har stanset fordi regi mistet dekningen
                    * leses ellers som at det er stille på settet. En lukket
                    * logg teller ikke minutter — den er ferdig.
                    */}
                  <span
                    className={`loggkort__alder mono${gammel ? " loggkort__alder--gammel" : ""}`}
                    title={s.sistePost === null ? "" : `Siste post ${klokke(s.sistePost)}`}
                  >
                    {!s.aapen ? "lukket"
                      : s.sistePost === null ? "ingen poster"
                        : alder(s.sistePost, naa)}
                  </span>
                </header>

                <Samtale
                  poster={s.poster.filter((p) => !p.slettet)}
                  skriver={skriver}
                  aapen={aapen}
                  utkast={utkast}
                  deltakere={deltakere}
                  deltakerId={deltakerId}
                  veileder={veileder}
                  onUtkast={setUtkast}
                  onAapne={aapne}
                  onDeltaker={setDeltakerId}
                  onSend={send}
                  onMerke={veksleMerke}
                />
              </section>
            );
          })}
        </div>
      </div>
    </div>
  );
}

/**
 * Samtalen i én boks.
 *
 * Skilt ut fordi rullingen hører til boksen: hver logg har sin egen bunn å
 * holde seg ved, og en felles ville dratt alle tre når én fikk en post.
 */
function Samtale({
  poster, skriver, aapen, utkast, deltakere, deltakerId, veileder,
  onUtkast, onAapne, onDeltaker, onSend, onMerke,
}: {
  readonly poster: readonly logg.Loggpost[];
  readonly skriver: boolean;
  /** Sann for den som faar kommentere, spoerre og gi hjerte. */
  readonly veileder: boolean;
  readonly aapen: Boks | null;
  readonly utkast: string;
  readonly deltakere: readonly su.Deltaker[];
  readonly deltakerId: string | null;
  onUtkast(v: string): void;
  onAapne(post: string, hva: "kommentar" | "synk"): void;
  onDeltaker(id: string | null): void;
  onSend(postId: string, hva: "kommentar" | "synk"): Promise<void>;
  onMerke(p: logg.Loggpost): Promise<void>;
}): JSX.Element {
  const boks = useRef<HTMLDivElement | null>(null);
  const nederst = useRef(true);

  /*
   * RULLER MED NÅR MAN ALT STÅR NEDERST.
   *
   * Leser redaktøren noe lenger opp, skal en ny post ikke rive blikket ned.
   */
  useEffect(() => {
    const el = boks.current;
    if (el === null || !nederst.current) return;
    el.scrollTop = el.scrollHeight;
  }, [poster.length, skriver]);

  const rull = (): void => {
    const el = boks.current;
    if (el === null) return;
    nederst.current = el.scrollHeight - el.scrollTop - el.clientHeight < 24;
  };

  return (
    <div className="samtale" ref={boks} onScroll={rull}>
      {poster.length === 0 && !skriver ? (
        <p className="samtale__tom">Ingenting logget.</p>
      ) : null}

      {poster.map((p) => {
        const svar = aapen !== null && aapen.post === p.id;

        return (
          <div className="bolk" key={p.id}>
            {/*
              * POSTEN, slik den ser ut i appen: fyll og ingen kant,
              * tidskoden øverst, etiketten ved siden av, teksten i mono.
              *
              * STRIPA ER HURTIGKNAPPENS FARGE, fanget i trykkøyeblikket
              * sammen med etiketten. Paletten er fast og ligger i
              * `apps/mobile/src/theme/tokens.ts` — seks farger som er prøvd
              * i dårlig lys — og verdien er ren heks, så den betyr det
              * samme her som på telefonen.
              *
              * Unntaket er SYNK-markøren, som appen setter til `var(--tc)`
              * for å følge paletten i blackout. Den løses mot nettflatas
              * eget token, og det er riktig: aksenten skal være flatas egen.
              */}
            <article className="post">
              {p.farge === null ? null : (
                <span className="post__stripe" style={{ background: p.farge }} />
              )}

              <div className="post__hode">
                <span className={`mono post__stempel${p.tcUsikker ? " post__stempel--usikker" : ""}`}>
                  {tidskode(p)}
                </span>
                {p.etikett === "" ? null : (
                  <span className="mono post__etikett">{p.etikett}</span>
                )}

                {/* HVEM SOM SKREV DENNE, naar det ikke er den loggen staar
                    i navnet til. Teamet deler logg, og da maa redaktoeren
                    kunne se at fotografen skrev linja om linsebyttet.
                    Null — og dermed usynlig — paa alle poster i en logg
                    med én forfatter: en forkortelse paa hver linje ville
                    vaert stoey. */}
                {p.skrevetAv === null ? null : (
                  <span className="mono post__hvem">{p.skrevetAv}</span>
                )}

                {/* Verktøyet viser seg når man peker på posten. Tre knapper
                    på hver post ville gjort loggen til et skjema. Og det er
                    redaktørens: se `kanVeilede`. */}
                {!veileder ? null : (
                <div className="post__verktoy">
                  <button
                    type="button"
                    className={`post__knapp${p.markert ? " post__knapp--paa" : ""}`}
                    onClick={() => void onMerke(p)}
                    aria-pressed={p.markert}
                    title={p.markert ? "Fjern hjertet" : "Gi posten et hjerte"}
                  >
                    ♥
                  </button>
                  <button
                    type="button"
                    className="post__knapp"
                    onClick={() => onAapne(p.id, "kommentar")}
                    title="Svar regi"
                  >
                    ✎
                  </button>
                  <button
                    type="button"
                    className="post__knapp"
                    onClick={() => onAapne(p.id, "synk")}
                    title="Skriv et synkspørsmål ut av posten"
                  >
                    ?
                  </button>
                </div>
                )}
              </div>

              {/* Markører uten tekst er ferdige poster. Ingenting telles
                  eller markeres som uferdig. Se CLAUDE.md. */}
              {p.tekst === "" ? null : <p className="mono post__tekst">{p.tekst}</p>}
            </article>

            {p.kommentarer.map((k) => (
              <div className="svar" key={k.id}>
                <p className="svar__tekst">{k.tekst}</p>
                <span className="svar__hvem">{k.forfatter}</span>
              </div>
            ))}

            {p.spoersmaal.map((q) => (
              <div className="svar svar--synk" key={q.id}>
                <span className="mono svar__etikett">SYNK · {q.deltaker ?? "ALLE"}</span>
                <p className="svar__tekst">{q.tekst}</p>
                <span className="svar__hvem">ligger i synklista</span>
              </div>
            ))}

            {svar ? (
              <div className="svarboks">
                  {aapen.hva === "synk" ? (
                    <select
                      className="svarboks__velger"
                      value={deltakerId ?? ""}
                      onChange={(e) => onDeltaker(e.target.value === "" ? null : e.target.value)}
                      aria-label="Deltaker"
                    >
                      <option value="">Til alle</option>
                      {deltakere.map((d) => (
                        <option key={d.id} value={d.id}>{d.forkortelse} · {d.navn}</option>
                      ))}
                    </select>
                  ) : null}
                  <textarea
                    className="svarboks__felt"
                    value={utkast}
                    autoFocus
                    rows={2}
                    placeholder={aapen.hva === "kommentar"
                      ? "Svar til regi"
                      : "Spørsmål til synken"}
                    onChange={(e) => onUtkast(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void onSend(p.id, aapen.hva);
                      }
                      if (e.key === "Escape") onAapne(p.id, aapen.hva);
                    }}
                  />
                <button
                  type="button"
                  className="svarboks__send"
                  onClick={() => void onSend(p.id, aapen.hva)}
                  disabled={utkast.trim() === ""}
                >
                  {aapen.hva === "kommentar" ? "Send til regi" : "Legg i synklista"}
                </button>
              </div>
            ) : null}
          </div>
        );
      })}

      {/*
        * TRE PRIKKER MENS REGI SKRIVER.
        *
        * Som i en meldingsapp, og av samme grunn: det som kommer er på vei,
        * og da venter man i stedet for å lure. Den kringkastes og lagres
        * aldri — en skriveindikator i en append-only logg ville vært en rad
        * per tastetrykk.
        */}
      {skriver ? (
        <div className="post post--skriver" aria-label="Regi skriver">
          <span /><span /><span />
        </div>
      ) : null}
    </div>
  );
}
